// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { ToastByStatus } from 'components/treactui-template/organisms/toast/ToastMessage';
import useProjectModelResetApi from 'hooks/useProjectModelResetApi';
import useProjectTrainingApi, { getProjectTrainingHistoryQueryKey } from 'hooks/useProjectTrainingApi';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { clearEvaluationProgressCache } from 'store/reducers/projectEvaluationSlice';
import { setPredictEnabled } from 'store/reducers/projectLabelSlice';
import { toggleResetModelDialog } from 'store/reducers/projectModelResetSlice';
import { fetchProjects } from 'store/reducers/projectsSlice';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, FooterButton } from '@tls/treact-ui';
import { IconLoading } from '@tls/treact-ui/icons';

export default function ResetModelDialog() {
  const { t } = useTranslation();

  const { id, show } = useAppSelector(s => s.resetModel);
  const { selectedProject } = useProjectsMetadata(id);
  const { projectName } = selectedProject ?? {};
  const { resetModel, loading } = useProjectModelResetApi();
  const { hasModel, loading: loadingHasModel } = useProjectTrainingApi(id ?? '', {
    enabled: show,
    showErrorToast: false,
  });
  const queryClient = useQueryClient();

  const dispatch = useAppDispatch();

  const onClose = useCallback(() => dispatch(toggleResetModelDialog()), [dispatch]);

  const resetProjectModel = useCallback(async () => {
    const { status } = await resetModel(id ?? '');
    ToastByStatus(
      status,
      t('project.settings.modelReset.title'),
      t('project.settings.modelReset.error', { name: projectName }),
      t('project.settings.modelReset.success', { name: projectName })
    );
    if (status === 200) {
      await queryClient.invalidateQueries({ queryKey: getProjectTrainingHistoryQueryKey(id ?? '') });
      dispatch(setPredictEnabled(false));
      dispatch(clearEvaluationProgressCache());
      dispatch(fetchProjects());
    }
    dispatch(toggleResetModelDialog());
  }, [dispatch, id, projectName, queryClient, resetModel, t]);

  let saveIcon: React.JSX.Element | undefined = <IconLoading />;
  if (!loading) {
    saveIcon = undefined;
  }

  let content: React.JSX.Element | string = <IconLoading />;
  if (!loadingHasModel) {
    content = hasModel
      ? t('project.settings.modelReset.details', { name: projectName })
      : t('project.settings.modelReset.noModelDetails', { name: projectName });
  }

  const footerButtons =
    !loadingHasModel && hasModel
      ? [
          <FooterButton
            key='cancel'
            label={t('cancel')}
            onClick={onClose}
            variant='secondary'
            data-testid='project-reset-dialog-cancel-button'
          />,
          <FooterButton
            icon={saveIcon}
            key='save'
            label={t('confirm')}
            disabled={loading || loadingHasModel}
            onClick={resetProjectModel}
            data-testid='project-reset-dialog-confirm-button'
          />,
        ]
      : [
          <FooterButton
            key='close'
            label={t('cancel')}
            onClick={onClose}
            variant='secondary'
            data-testid='project-reset-dialog-close-button'
          />,
        ];

  return (
    <Dialog enableClosing open={show} size={'small'} onClose={onClose} data-testid='project-reset-dialog'>
      <Dialog.DialogHeader label={t('project.settings.modelReset.title', { name: projectName })} showCloseButton />
      <Dialog.DialogContent>
        <div data-testid='project-reset-dialog-content'>{content}</div>
      </Dialog.DialogContent>
      <Dialog.Footer right={footerButtons} />
    </Dialog>
  );
}
